import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  Button,
} from '@mui/material';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import { recipeV2Client, RecipeAudienceDetail } from '../../../app/services/api/recipeV2';
import { PictureTile } from '../../../components/shared/tiles/PictureTile';
import RecipeBankAllergensTab from './RecipeBankAllergensTab';
import RecipeBankDietaryTab from './RecipeBankDietaryTab';
import RecipeBankNutritionTab from './RecipeBankNutritionTab';

interface RecipeAudiencePreviewDialogProps {
  open: boolean;
  recipeId: number;
  onClose: () => void;
}

const RecipeAudiencePreviewDialog = ({
  open,
  recipeId,
  onClose,
}: RecipeAudiencePreviewDialogProps) => {
  const { t } = useTranslation('recipeV2');
  const [recipe, setRecipe] = useState<RecipeAudienceDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setRecipe(null);
      setError(null);
      return;
    }

    let active = true;
    setIsLoading(true);
    setError(null);
    recipeV2Client.getRecipeAudienceDetail(recipeId)
      .then((value) => { if (active) setRecipe(value); })
      .catch((cause) => {
        if (active) setError(cause instanceof Error ? cause.message : t('notFound'));
      })
      .finally(() => { if (active) setIsLoading(false); });

    return () => { active = false; };
  }, [open, recipeId, t]);

  return (
    <Dialog open={open}
      disableRestoreFocus
      onClose={onClose}
      maxWidth="md"
      fullWidth>
      <DialogTitle>{t('recipeBank.customerView', 'Customer view')}</DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Typography color="error">{error}</Typography>}

        {!isLoading && recipe && (
          <Stack spacing={4}>
            <Stack direction="row"
              spacing={2}
              alignItems="center">
              <Typography variant="textXL"
                fontWeight={700}>
                {recipe.description}
              </Typography>
              {recipe.primaryCategory && <Chip label={recipe.primaryCategory}
                size="small" />}
            </Stack>

            <PictureTile
              src={recipe.mediaUrl ?? null}
              alt={recipe.description}
              objectFit="cover"
              fallbackLabel={t('recipeBank.noImage', 'No recipe image')}
              fallback={(
                <Stack alignItems="center"
                  spacing={1}>
                  <ImageOutlined sx={{ fontSize: 40 }} />
                  <Typography variant="textSM">
                    {t('recipeBank.noImage', 'No recipe image')}
                  </Typography>
                </Stack>
              )}
              sx={{ height: 300 }}
            />

            {recipe.narrativeDescription && (
              <Typography color="text.secondary">
                {recipe.narrativeDescription}
              </Typography>
            )}

            <Box>
              <Typography variant="textMD"
                fontWeight={600}
                sx={{ mb: 2 }}>
                {t('tabs.allergens')}
              </Typography>
              <RecipeBankAllergensTab allergens={recipe.allergenSummary} />
            </Box>

            <Box>
              <Typography variant="textMD"
                fontWeight={600}
                sx={{ mb: 2 }}>
                {t('tabs.dietary')}
              </Typography>
              <RecipeBankDietaryTab attributes={recipe.dietaryAttributes} />
            </Box>

            <Box>
              <Typography variant="textMD"
                fontWeight={600}
                sx={{ mb: 2 }}>
                {t('tabs.nutrition')}
              </Typography>
              <RecipeBankNutritionTab nutrition={recipe.nutrition} />
            </Box>

            <Typography variant="textXS"
              color="text.secondary">
              {t(
                'recipeBank.customerViewRestricted',
                'This view intentionally excludes ingredient quantities, costing, suppliers, audit information and other operational fields.',
              )}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipeAudiencePreviewDialog;
